import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentDisclosuresV2Data,
  type EnrollmentDisclosuresV2SubmitInput,
  type EnrollmentDisclosuresV2SubmitOutput,
} from '../../../../../models'
import { createMutationHook, createQueryHook } from '../../../../../hooks'

/**
 * Get Enrollment DisclosuresV2 data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentDisclosuresV2Data>
 * @example const response = await getEnrollmentDisclosuresV2Data()
 */
export const getEnrollmentDisclosuresV2Data = async (
  context?: DefaultContext,
): Promise<EnrollmentDisclosuresV2Data> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentDisclosuresV2Data',
      query: `mutation getEnrollmentDisclosuresV2Data($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              progress
              userInfo {
                investment {
                  disclosures {
                    disclosureId
                    disclosureName
                    disclosureUri
                  }
                }
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentDisclosuresV2Data }
  return response.submitEnrollment
}

export const useGetEnrollmentDisclosuresV2Data = createQueryHook<undefined, EnrollmentDisclosuresV2Data>(
  getEnrollmentDisclosuresV2Data,
)

/**
 * Submit Enrollment DisclosuresV2 data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentDisclosuresV2SubmitOutput>
 * @example const response = await submitEnrollmentDisclosuresV2Data();
 */
export const submitEnrollmentDisclosuresV2Data = async (
  enrollmentDisclosuresV2SubmitInput: EnrollmentDisclosuresV2SubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentDisclosuresV2SubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentDisclosuresV2Data',
      query: `mutation submitEnrollmentDisclosuresV2Data($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentDisclosuresV2SubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentDisclosuresV2SubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentDisclosuresV2Data = createMutationHook<
  EnrollmentDisclosuresV2SubmitInput,
  EnrollmentDisclosuresV2SubmitOutput
>(submitEnrollmentDisclosuresV2Data)
